/**
 * ChartCard — wrapper for every chart in Insights.
 *
 * Provides:
 *   - Title + optional icon
 *   - Optional "so what" caption
 *   - Optional toolbar (right side, for filters / view toggles)
 *   - Optional drill-down link
 *
 * All charts have the same chrome so the dashboard feels consistent.
 */

import { Link } from '@tanstack/react-router';
import { ArrowRight } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { SoWhatCaption } from './so-what-caption';

export interface ChartCardProps {
  title: string;
  icon?: LucideIcon;
  caption?: string;
  toolbar?: React.ReactNode;
  drillDownUrl?: string;
  drillDownLabel?: string;
  className?: string;
  bodyClassName?: string;
  children: React.ReactNode;
}

export function ChartCard({
  title, icon: Icon, caption, toolbar, drillDownUrl, drillDownLabel = 'Drill in',
  className, bodyClassName, children,
}: ChartCardProps) {
  return (
    <Card className={cn('flex flex-col', className)}>
      <CardHeader className="pb-2 pt-4 px-5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <CardTitle className="text-sm font-semibold flex items-center gap-1.5">
              {Icon && <Icon className="h-4 w-4 text-muted-foreground" />}
              {title}
            </CardTitle>
            {caption && <SoWhatCaption text={caption} className="mt-1.5" />}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {toolbar}
            {drillDownUrl && (
              <Link
                to={drillDownUrl as any}
                className="text-[11px] font-medium text-primary hover:underline inline-flex items-center gap-0.5"
              >
                {drillDownLabel}
                <ArrowRight className="h-3 w-3" />
              </Link>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className={cn('flex-1 px-5 pb-4 pt-1', bodyClassName)}>
        {children}
      </CardContent>
    </Card>
  );
}
